import React from "react";
import { TableCard } from "duck/ui/chat/components/renderers/components/TableCard";
import { useChatContext } from "duck/ui/chat/contexts/useChatContext";
import {
  AssistantMessageContent,
  DuckValueFormat,
  MessageType,
  TableData,
} from "duck/ui/chat/types";
import { useTranslation } from "react-i18next";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";

import { DataType } from "shared/types";
import { isNumeric, randomID } from "shared/utils";

import IconButton from "features/components/IconButton";
import Typography from "features/components/Typography";
import MaterialTable, {
  CustomTableToolbar,
  RowData,
  SchemaEntry,
} from "features/ui/MaterialTable";

import { prepareDuckTableValue } from "./components/SubTable";
import { Renderer, RendererContext } from "./types";

const MAX_TABLE_WIDTH = "80vw";

const isNumericColumn = (rows: string[][], columnIndex: number): boolean => {
  if (!rows || rows.length === 0) return false;

  return rows.every((row) => {
    const value = row[columnIndex];

    return isNumeric(value);
  });
};

const VALUE_FORMAT_TO_DATA_TYPE: Record<DuckValueFormat, DataType> = {
  text: DataType.VERBATIM,
  number: DataType.NUMBER,
  integer: DataType.NUMBER,
  ratio_percentage: DataType.RATIO_PERCENTAGE,
  boolean: DataType.BOOLEAN,
  date: DataType.DATE,
  date_with_time: DataType.DATE_WITH_TIME,
  year_month: DataType.DATE_YEAR_MONTH,
  year_month_day: DataType.DATE_YEAR_MONTH_DAY,
};

// The agent states the format, but it can be absent or unrecognized: these types mirror the server's by hand,
// and a table streams in as JSON patches, so a header can arrive before its format does. Either way, fall back
// to inferring the type from the values.
const columnDataType = (
  valueFormat: DuckValueFormat | null | undefined,
  rows: string[][],
  columnIndex: number
): DataType => {
  // valueFormat is missing in older messages
  if (valueFormat && valueFormat in VALUE_FORMAT_TO_DATA_TYPE) {
    return VALUE_FORMAT_TO_DATA_TYPE[valueFormat];
  }

  return isNumericColumn(rows, columnIndex) ? DataType.NUMBER : DataType.STRING;
};

// formatValue only formats a number it is given as a number, and its boolean branch tests plain truthiness,
// which the string "false" would pass.
const NUMERIC_DATA_TYPES = [
  DataType.NUMBER,
  DataType.PERCENTAGE,
  DataType.RATIO_PERCENTAGE,
];

const prepareDuckTableData = (
  rows: string[][],
  schema: SchemaEntry[]
): RowData[] => {
  let data: RowData[] = [];
  if (Array.isArray(rows) && rows.length > 0) {
    data = (rows as string[][]).map((rowArr) => {
      const obj: Record<string, any> = {};
      obj["row_id"] = randomID();

      schema.forEach((schemaEntry, i) => {
        const key = schemaEntry.accessor;
        let value = prepareDuckTableValue(key, rowArr[i]);

        if (value !== null && value !== undefined && value !== "") {
          if (NUMERIC_DATA_TYPES.includes(schemaEntry.dataType)) {
            const num = Number(value);
            if (!isNaN(num) && isFinite(num)) {
              value = num;
            }
          } else if (
            schemaEntry.dataType === DataType.BOOLEAN &&
            typeof value === "string"
          ) {
            value = /^(true|1)$/i.test(value);
          }
        }

        obj[key] = value;
      });

      return obj;
    });
  }

  return data;
};

const hasTableData = (content: AssistantMessageContent): boolean =>
  content.message_type === MessageType.TABLE &&
  "headers" in content &&
  "rows" in content &&
  Boolean(content.headers) &&
  Boolean(content.rows) &&
  Array.isArray(content.headers) &&
  Array.isArray(content.rows) &&
  content.headers.length > 0;

export const TableRenderer: Renderer<AssistantMessageContent> = {
  type: "table",

  canRender: (content: AssistantMessageContent): boolean =>
    hasTableData(content),

  render: (
    content: AssistantMessageContent,
    context?: RendererContext
  ): React.ReactNode => {
    if (!hasTableData(content)) return null;

    const {
      title,
      headers,
      rows,
      identifier,
      suitable_for_chart,
      renderer = "table",
    } = content as TableData;

    const schema: SchemaEntry[] = headers.map((header, index) => {
      if (typeof header === "string") {
        return {
          label: header,
          accessor: header,
          dataType: columnDataType(null, rows, index),
        };
      }

      return {
        label: header.label,
        accessor: header.column_name || header.label,
        dataType: columnDataType(header.value_format, rows, index),
        decimals: header.value_format === "integer" ? 0 : undefined,
        columnWidth: header.column_width || undefined,
      };
    });

    // Transform rows into data for MaterialTable
    const data = prepareDuckTableData(rows, schema);

    if (["key-value-display", "card"].includes(renderer)) {
      return (
        <TableCard
          data={data}
          accessors={schema.map((x) => x.accessor)}
          title={title}
        />
      );
    }

    const toolbar = () => (
      <CustomTableToolbar
        rightContent={
          <DuckTableToolbar
            suitable_for_chart={suitable_for_chart}
            identifier={identifier}
          />
        }
      >
        {title && (
          <Typography variant="h5" className="!ml-[6px]">
            {title}
          </Typography>
        )}
      </CustomTableToolbar>
    );

    return (
      <div className="mt-4 mb-4">
        <MaterialTable
          schema={schema}
          toolbar={toolbar}
          data={data}
          pageKey={`duck-table-${identifier || randomID()}`}
          getRowId={({ row_id }) => row_id}
          disableColumnMenu
          disableColumnSorting
          hideFooter
          dynamicTableWidth={Boolean(context?.isFullscreen)}
          maxTableWidth={MAX_TABLE_WIDTH}
        />
      </div>
    );
  },
};

const DuckTableToolbar = ({
  suitable_for_chart,
  identifier,
}: {
  suitable_for_chart?: boolean;
  identifier?: string;
}) => {
  const { t } = useTranslation("duck");
  const { sendMessage } = useChatContext();

  return (
    <div className="flex gap-x-1 ml-1">
      {identifier && (
        <IconButton
          title={t("download_data_as_csv")}
          data-testid="download-table-csv-duck"
          onClick={() => {
            sendMessage(
              "Download the data in the table with identifier " +
                identifier +
                " as CSV."
            );
          }}
          color="secondary"
          size="small"
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
      )}

      {identifier && suitable_for_chart && (
        <IconButton
          title={t("create_chart_from_data")}
          onClick={() => {
            sendMessage(
              "Create a chart showing the data in the table with identifier " +
                identifier +
                "."
            );
          }}
          color="secondary"
          size="small"
        >
          <InsertChartOutlinedIcon />
        </IconButton>
      )}
    </div>
  );
};
